import { ErrorResponse } from "@gtpl/shared-models/common-models";
import { ContractRequest, ContractResponseModel } from "@gtpl/shared-models/hrms";
import { Injectable } from "@nestjs/common";
import { InjectRepository } from "@nestjs/typeorm";
import { Repository } from "typeorm";
import { ContractorDto } from "./dto/human-resource-agent.dto";
import { Contractor } from "./entity/human-resource-agent.entity";
import { ContractorAdapter } from "./human-resource-agent.adapter";

@Injectable()
export class ContractorService {
    constructor(
        @InjectRepository(Contractor)
        private repo: Repository<Contractor>,
        private adapter: ContractorAdapter,
    ) { }

    
    async getContractorDetails(contractorId: number): Promise<Contractor> {
        const contractResponse = await this.repo.findOne({
            where: { contractorId: contractorId },
        });
        if (contractResponse) {
            return contractResponse;
        } else {
            return null;
        }
    }

    async createContractor(dto: ContractorDto, isUpdate: boolean): Promise<ContractResponseModel> {
        try {
            if (!isUpdate) {
                const entity = await this.getContractorDetails(dto.contractorId);
                if (entity) {
                    throw new ErrorResponse(11104, 'Contractor already exists');
                }
            } else {
                const entity = await this.getContractorDetails(dto.contractorId);
                if (entity) {
                    if (entity.contractorId != dto.contractorId) {
                        throw new ErrorResponse(11104, 'Contractor already exists')
                    }
                }
            }
            const convertedEntity: Contractor = this.adapter.convertDtoToEntity(dto, isUpdate);
            const savedEntity: Contractor = await this.repo.save(convertedEntity);
            const savedDto: ContractorDto = this.adapter.convertEntityToDto(savedEntity);
            if (savedDto) {
                const response = new ContractResponseModel(true, 1, isUpdate ? 'Contractor Updated Successfully' : 'Contractor Created Successfully');
                return response;
            } else {
                throw new ErrorResponse(11106, 'Contractor saved but issue while transforming it into DTO');
            }
        } catch (error) {
            throw error;
        }
    }

    async getAllContractorActions(): Promise<ContractResponseModel> {
        try {
            const conDto: ContractorDto[] = [];
            const conEntity: Contractor[] = await this.repo.find({ order: { contractorId: 'DESC' },where :{isActive : true} });
            if (conEntity.length) {
                conEntity.forEach(Entity => {
                    const convertedDto: Contractor = this.adapter.convertEntityToDto(Entity);
                    conDto.push(convertedDto);
                });
                const response = new ContractResponseModel(true, 1, 'Contractor Retrieved Successfully',conDto);
                return response;
            } else {
                throw new ErrorResponse(99998, 'Data Not Found');
            }
        } catch (err) {
            throw err;
        }
    }

    
    async getAllActiveContractActions(): Promise<ContractResponseModel> {
        try {
            const conDto: ContractorDto[] = [];
            const conEntity: Contractor[] = await this.repo.find({ order: { contractorId: 'ASC' }, where: { isActive: true }, });
            if (conEntity) {
                conEntity.forEach(Entity => {
                    const convertedEntity: ContractorDto = this.adapter.convertEntityToDto(Entity);
                    conDto.push(convertedEntity);
                });
                const response = new ContractResponseModel(true, 11108, 'Active Contractor Retrieved Successfully',conDto);
                return response;
            } else {
                throw new ErrorResponse(99998, 'Data Not Found');
            }
        } catch (err) {
            throw err;
        }
    }

    
    async getContractById(contractorId: number): Promise<Contractor> {
        const response = await this.repo.findOne({
            where: { contractorId: contractorId },
        });
        if (response) {
            return response;
        } else {
            return null;
        }
    }

    async activateOrDeactivateContractor(conReq: ContractRequest): Promise<ContractResponseModel> {
        try {
            const conExists = await this.getContractById(conReq.contractorId);
            if (conExists) {
                if (conReq.versionFlag != conExists.versionFlag) {
                    throw new ErrorResponse(10113, 'Someone updated the current Contractor information. Refresh and try again');
                } else {
                    const conStatus = await this.repo.update(
                        { contractorId: conReq.contractorId },
                        { isActive: conReq.isActive, updatedUser: conReq.updatedUser }
                    );
                    if (conExists.isActive) {
                        if (conStatus.affected) {
                            const conResponse: ContractResponseModel = new ContractResponseModel(true, 10115, 'Contractor is de-activated successfully');
                            return conResponse;
                        } else {
                            throw new ErrorResponse(10111, ' Contractor is already de-activated');
                        }
                    } else {
                        if (conStatus.affected) {
                            const conResponse: ContractResponseModel = new ContractResponseModel(true, 10114, 'Contractor is activated successfully');
                            return conResponse;
                        } else {
                            throw new ErrorResponse(10112, 'Contractor is already activated');
                        }
                    }
                }
            } else {
                throw new ErrorResponse(99998, 'No Records Found');
            }
        } catch (err) {
            throw err;
        }
    }
}